import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, of, delay, tap, map } from 'rxjs';
import { environment } from 'src/environments/environment';
import { NotificationService } from '@progress/kendo-angular-notification';

// Import mock data
import { customersMockData, CustomerData } from 'src/app/features/customer/reporting-v2/sample/customers-mock-data';
import {
  dashboardStatsMockData,
  DashboardStats,
} from 'src/app/features/customer/reporting-v2/components/dashboard/reporting-dashboard-mock-data/dashboard-stats-mock-data';
import {
  reportTemplatesMockData,
  ReportTemplate,
} from 'src/app/features/customer/reporting-v2/components/generate/reporting-generate-mock-data/report-templates-mock-data';

/**
 * Enhanced Reporting Service for MSP V2 Reporting
 * Supports multi-customer operations and bulk report generation
 */
@Injectable({
  providedIn: 'root',
})
export class ReportingService {
  private http = inject(HttpClient);
  private notificationService = inject(NotificationService);
  private apiUrl = environment.apiUrl;

  /**
   * Legacy method - Get reports for a specific organization
   * Used by legacy reporting component (Type 1 customers)
   */
  public getReports(organizationId: number | null): Observable<any[]> {
    if (!organizationId) {
      return of([]);
    }
    return this.http.get<any[]>(`${this.apiUrl}/v1/reporting/templates/?organization_id=${organizationId}`).pipe();
  }

  /**
   * Get all customers for the MSP
   * @returns Observable of customer list
   */
  public getCustomers(): Observable<CustomerData[]> {
    // Mock implementation - return mock data
    return of(customersMockData).pipe(
      delay(300), // Simulate network delay
      tap(() => {
        console.log('[Mock] Fetched customers from backend');
      })
    );

    // Production implementation (commented for prototype):
    // return this.http.get<CustomerData[]>(`${this.apiUrl}/v2/reporting/customers/`);
  }

  /**
   * Get active customers only
   * @returns Observable of active customer list
   */
  public getActiveCustomers(organizationId: number): Observable<Customer[]> {
    const baseUrl = `${this.apiUrl}/v1/organization/administrative/organization(${organizationId})/list_child_organizations/?$orderby=name&$top=1000&$filter=status eq 1`;
    return this.http.get<{ value: any[] }>(baseUrl).pipe(
      map(response => {
        return response.value.map(item => ({
          id: item.id,
          name: item.name,
          industry: item.industry?.name || ''
        }));
      })
    );

  }

  /**
   * Get customers by compliance framework
   * @param framework - Compliance framework name (e.g., 'HIPAA', 'NIST 800-171')
   * @returns Observable of customer list
   */
  public getCustomersByCompliance(framework: string): Observable<CustomerData[]> {
    // Mock implementation
    return of(customersMockData.filter((c) => c.compliance_frameworks?.includes(framework))).pipe(
      delay(300),
      tap(() => {
        console.log(`[Mock] Fetched customers with ${framework} compliance from backend`);
      })
    );

    // Production implementation (commented for prototype):
    // return this.http.get<CustomerData[]>(`${this.apiUrl}/v2/reporting/customers/?compliance=${framework}`);
  }

  /**
   * Get dashboard statistics
   * @returns Observable of dashboard stats
   */
  public getDashboardStats(): Observable<DashboardStats> {
    // Mock implementation
    return of(dashboardStatsMockData).pipe(
      delay(300),
      tap(() => {
        console.log('[Mock] Fetched dashboard stats from backend');
      })
    );

    // Production implementation (commented for prototype):
    // return this.http.get<DashboardStats>(`${this.apiUrl}/v2/reporting/dashboard/stats/`);
  }

  /**
   * Get saved report templates
   * @param includePersonal - Include personal templates for current user
   * @returns Observable of template list
   */
  public getReportTemplates(includePersonal: boolean = true): Observable<ReportTemplate[]> {
    // Mock implementation
    let templates = includePersonal
      ? reportTemplatesMockData
      : reportTemplatesMockData.filter((t) => t.is_tenant_level);

    return of(templates).pipe(
      delay(300),
      tap(() => {
        console.log('[Mock] Fetched report templates from backend');
      })
    );

    // Production implementation (commented for prototype):
    // return this.http.get<ReportTemplate[]>(`${this.apiUrl}/v2/reporting/templates/?include_personal=${includePersonal}`);
  }

  /**
   * Get template by ID
   * @param templateId - Template ID
   * @returns Observable of template
   */
  public getTemplateById(templateId: string): Observable<ReportTemplate | null> {
    // Mock implementation
    const template = reportTemplatesMockData.find((t) => t.id === templateId);
    return of(template || null).pipe(
      delay(200),
      tap(() => {
        console.log(`[Mock] Fetched template ${templateId} from backend`);
      })
    );

    // Production implementation (commented for prototype):
    // return this.http.get<ReportTemplate>(`${this.apiUrl}/v2/reporting/templates/${templateId}/`);
  }

  /**
   * Save a new report template
   * @param template - Template data
   * @returns Observable of created template
   */
  public saveReportTemplate(template: Partial<ReportTemplate>): Observable<ReportTemplate> {
    // Mock implementation
    const newTemplate: ReportTemplate = {
      id: `rt-${Date.now()}`,
      name: template.name || 'New Template',
      report_type: template.report_type || '',
      report_type_name: template.report_type_name || '',
      is_tenant_level: template.is_tenant_level || false,
      created_by: 'current-user@msp.com',
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
      configuration: template.configuration || {
        include_executive_summary: true,
        include_detailed_findings: true,
        include_remediation: false,
        delivery_method: 'download',
      },
      usage_count: 0,
      last_used: null,
    };

    return of(newTemplate).pipe(
      delay(500),
      tap(() => {
        this.notificationService.show({
          content: `Template "${newTemplate.name}" saved successfully! (Backend would POST to /v2/reporting/templates/)`,
          cssClass: 'success-notification',
          animation: { type: 'fade', duration: 400 },
          position: { horizontal: 'center', vertical: 'top' },
          type: { style: 'success', icon: true },
          hideAfter: 5000,
        });
        console.log('[Mock] Saved report template to backend');
      })
    );

    // Production implementation (commented for prototype):
    // return this.http.post<ReportTemplate>(`${this.apiUrl}/v2/reporting/templates/`, template);
  }

  /**
   * Update an existing report template
   * @param templateId - Template ID
   * @param updates - Template updates
   * @returns Observable of updated template
   */
  public updateReportTemplate(templateId: string, updates: Partial<ReportTemplate>): Observable<ReportTemplate> {
    // Mock implementation
    const existingTemplate = reportTemplatesMockData.find((t) => t.id === templateId);
    const updatedTemplate: ReportTemplate = {
      ...(existingTemplate || ({} as ReportTemplate)),
      ...updates,
      id: templateId,
      updated_at: new Date().toISOString(),
    };

    return of(updatedTemplate).pipe(
      delay(500),
      tap(() => {
        this.notificationService.show({
          content: `Template "${updatedTemplate.name}" updated successfully! (Backend would PUT to /v2/reporting/templates/${templateId}/)`,
          cssClass: 'success-notification',
          animation: { type: 'fade', duration: 400 },
          position: { horizontal: 'center', vertical: 'top' },
          type: { style: 'success', icon: true },
          hideAfter: 5000,
        });
        console.log(`[Mock] Updated template ${templateId} in backend`);
      })
    );

    // Production implementation (commented for prototype):
    // return this.http.put<ReportTemplate>(`${this.apiUrl}/v2/reporting/templates/${templateId}/`, updates);
  }

  /**
   * Delete a report template
   * @param templateId - Template ID
   * @returns Observable of success
   */
  public deleteReportTemplate(templateId: string): Observable<void> {
    // Mock implementation
    return of(void 0).pipe(
      delay(500),
      tap(() => {
        this.notificationService.show({
          content: `Template deleted successfully! (Backend would DELETE /v2/reporting/templates/${templateId}/)`,
          cssClass: 'success-notification',
          animation: { type: 'fade', duration: 400 },
          position: { horizontal: 'center', vertical: 'top' },
          type: { style: 'success', icon: true },
          hideAfter: 5000,
        });
        console.log(`[Mock] Deleted template ${templateId} from backend`);
      })
    );

    // Production implementation (commented for prototype):
    // return this.http.delete<void>(`${this.apiUrl}/v2/reporting/templates/${templateId}/`);
  }

  /**
   * Fetches all report categories.
   * @returns {Observable<ReportCategories[]>} Emits an array of ReportCategories objects
   */
  public getReportCategories(): Observable<ReportCategories[]> {
    const baseUrl = `${this.apiUrl}/v2/reporting/api/report-categories/`;
    return this.http.get<{ value: ReportCategories[] }>(baseUrl).pipe(
      map((response) => {
        return [{ id: 0, category: 'All' }, ...response.value];
      })
    );
  }

  /**
   * Fetches all report compliance frameworks.
   * @returns {Observable<string[]>} Emits an array containing only `compliance_framework` field
   * extracted from each `ReportComplianceFrameworks` object.
   */
  public getReportComplianceFrameworks(): Observable<ReportComplianceFrameworks[]> {
    const baseUrl = `${this.apiUrl}/v2/reporting/api/report-compliance-frameworks/`;
    return this.http.get<{ value: ReportComplianceFrameworks[] }>(baseUrl).pipe(
      map((response): ReportComplianceFrameworks[] => [
      { id: 'all', compliance_framework: 'All' },
      ...response.value
    ])
    );
  }

  /**
   * Fetches all report compliance tags or filter by compliance framework id.
   * @returns {Observable<ReportComplianceTags[]>} Emits an array containing only ReportComplianceTags objects
   */
  public getReportComplianceTags(compliance_framework_id?: number | string): Observable<ReportComplianceTags[]> {
    let baseUrl = `${this.apiUrl}/v2/reporting/api/report-compliance-tags`;
    if (compliance_framework_id !== 'all' && typeof compliance_framework_id === 'number') {
      baseUrl += `?$filter=compliance_framework eq ${compliance_framework_id}`;
    }
    return this.http.get<{ value: any[] }>(baseUrl).pipe(
      map(response => {
        return response.value.map(item => ({
          id: item.id,
          name: item.compliance_tag,
          category_id: item.compliance_framework.id,
          category: item.compliance_framework.compliance_framework,
          description: '',
          color: item.color
        }));
      })
    );
  }

  /**
   * Fetches all report types with optional category filter.
   * @param categoryId - Optional category ID for server-side filtering
   * @returns {Observable<ReportTypes[]>} Emits an array containing only ReportTypes objects
   */
  public getReportTypes(categoryId?: number): Observable<ReportTypes[]> {
    let baseUrl = `${this.apiUrl}/v2/reporting/api/report-types/`;
    if (categoryId) {
      baseUrl += `?$filter=report_template__report_category eq ${categoryId}`;
    }
    return this.http.get<{ value: any[] }>(baseUrl).pipe(
      map(response => {
        return response.value.map(item => ({
          id: item.id,
          name: item.name,
          description: item.description,
          format: item.output_formats,
          reportTemplate: {
            id: item.report_template.id,
            reportType: item.report_template.report_type,
            complianceTags: item.report_template.compliance_tags.map((tag: any) => ({
              id: tag.id,
              name: tag.compliance_tag,
              category_id: tag.compliance_framework.id,
              category: tag.compliance_framework.compliance_framework,
              description: '',
              color: tag.color
            })),
            categories: item.report_template.report_category.map((cat: any) => ({
              id: cat.id,
              category: cat.category
            }))
          }
        }));
      })
    );
  }
}


export interface ReportCategories {
  id: number;
  category: string;
}

export interface ReportComplianceFrameworks {
  id: string | number;
  compliance_framework: string;
}

export interface ReportComplianceTags {
  id: number;
  name: string;
  category_id: number;
  category: string;
  description: string;
  color: string;
}

export interface ReportTemplateCategory {
  id: number;
  category: string;
}

export interface ReportTemplates {
  id: number;
  reportType: string;
  complianceTags: ReportComplianceTags[];
  categories: ReportTemplateCategory[];
}

export interface ReportTypes {
  id: number;
  name: string;
  description: string;
  format: string;
  reportTemplate: ReportTemplates;
}

export interface Customer {
  id: number;
  name: string;
  industry: string;
}
